import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, ArrowLeftRight, Edit2, Copy, Trash2, FileDown, Filter, X, Search, Columns3 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useProject } from '../context/ProjectContext';
import { useConfirm } from '../context/ConfirmContext';
import { useDataVersion } from '../context/DataVersionContext';
import { formatBRL, formatDateBR } from '../lib/format';
import TransactionDetailsModal from '../components/TransactionDetailsModal';
import TransactionFormModal from '../components/TransactionFormModal';
import FilterBar, { EMPTY_FILTERS, filtersToParams, countActiveFilters } from '../components/FilterBar';
import SummaryCards from '../components/SummaryCards';
import SelectionSummary from '../components/SelectionSummary';
import ColumnsPanel from '../components/ColumnsPanel';
import { useColumnPrefs } from '../lib/useColumnPrefs';
import Pagination from '../components/Pagination';
import { exportTransactionsPDF } from '../lib/exportPdf';

const PAGE_SIZE = 100;

const COLUMNS = [
  { id: 'date', label: 'Data', width: '90px' },
  { id: 'description', label: 'Descrição', width: 'auto' },
  { id: 'pot_exit', label: 'Pote Saída', width: '115px' },
  { id: 'pot_entry', label: 'Pote Entrada', width: '115px' },
  { id: 'account_exit', label: 'Conta Saída', width: '115px' },
  { id: 'account_entry', label: 'Conta Entrada', width: '120px' },
  { id: 'entity', label: 'Pessoas', width: '100px' },
  { id: 'source', label: 'Fonte de Receita', width: '120px' },
  { id: 'tags', label: 'Tags' },
  { id: 'amount', label: 'Valor', width: '120px' },
];
const DEFAULT_COLS = ['date', 'description', 'pot_exit', 'pot_entry', 'account_exit', 'account_entry', 'tags', 'amount'];

const CONFIG = {
  pots: { resourceList: 'pots', label: 'Pote', filterKey: 'pot_id', balanceKey: true },
  accounts: { resourceList: 'accounts', label: 'Conta', filterKey: 'account_id', balanceKey: true },
  entities: { resourceList: 'entities', label: 'Entidade', filterKey: 'entity_id', balanceKey: false },
  'revenue-sources': { resourceList: 'revenue_sources', label: 'Fonte', filterKey: 'revenue_source_id', balanceKey: false },
};

export default function ResourceTransactions({ kind }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const confirm = useConfirm();
  const { bump, version } = useDataVersion();
  const [item, setItem] = useState(null);
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0 });
  const [page, setPage] = useState(1);
  const [pots, setPots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [visibleCols, toggleCol, resetCols] = useColumnPrefs('dpots:tx-columns:v1', DEFAULT_COLS, COLUMNS);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Âncora do Shift+clique — ver o comentário equivalente em Transactions.jsx
  const [ancora, setAncora] = useState(null);

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clicarCaixa = (e, indice) => {
    const alvo = txs[indice];
    if (!alvo) return;
    if (e.shiftKey && ancora != null && ancora !== indice && txs[ancora]) {
      const ini = Math.min(ancora, indice);
      const fim = Math.max(ancora, indice);
      const marcar = !selectedIds.has(alvo.id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = ini; i <= fim; i++) {
          if (!txs[i]) continue;
          if (marcar) next.add(txs[i].id); else next.delete(txs[i].id);
        }
        return next;
      });
    } else {
      toggleSelectOne(alvo.id);
    }
    setAncora(indice);
  };

  const evitarSelecaoDeTexto = (e) => { if (e.shiftKey) e.preventDefault(); };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSel = txs.length > 0 && txs.every((t) => prev.has(t.id));
      if (allSel) {
        const next = new Set(prev);
        txs.forEach((t) => next.delete(t.id));
        return next;
      }
      const next = new Set(prev);
      txs.forEach((t) => next.add(t.id));
      return next;
    });
  };
  const clearSelection = () => { setSelectedIds(new Set()); setAncora(null); };
  const allOnPageSelected = txs.length > 0 && txs.every((t) => selectedIds.has(t.id));

  const selectionTotals = (() => {
    let income = 0, expense = 0, transfer = 0, transferCount = 0;
    for (const t of txs) {
      if (!selectedIds.has(t.id)) continue;
      if (t.type === 'income') income += t.amount || 0;
      else if (t.type === 'expense') expense += t.amount || 0;
      else if (t.type === 'transfer') { transfer += t.amount || 0; transferCount += 1; }
    }
    return { income, expense, balance: income - expense, transfer, transferCount };
  })();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formState, setFormState] = useState(null);
  const cfg = CONFIG[kind];
  const fixed = { [cfg.filterKey]: parseInt(id) };
  const activeFilters = countActiveFilters(filters);
  const clearFilters = () => setFilters({ ...EMPTY_FILTERS, search: filters.search });

  useEffect(() => { setPage(1); }, [filters, id]);
  // Índices mudam de significado ao trocar página/filtro/recurso
  useEffect(() => { setAncora(null); }, [page, filters, id]);

  const loadAux = useCallback(async () => {
    if (!currentProject) return;
    const pid = currentProject.id;
    const [list, p, a, e, s, t] = await Promise.all([
      api.get(`/projects/${pid}/${cfg.resourceList}`),
      api.get(`/projects/${pid}/pots`),
      api.get(`/projects/${pid}/accounts`),
      api.get(`/projects/${pid}/entities`),
      api.get(`/projects/${pid}/revenue_sources`),
      api.get(`/projects/${pid}/tags`),
    ]);
    setItem(list.data.find((x) => x.id === parseInt(id)));
    setPots(p.data); setAccounts(a.data); setEntities(e.data); setSources(s.data); setTags(t.data);
  }, [currentProject, id, cfg.resourceList]);

  const load = useCallback(async (signal) => {
    if (!currentProject) return;
    setLoading(true);
    try {
      const params = { ...fixed, ...filtersToParams(filters), limit: PAGE_SIZE, skip: (page - 1) * PAGE_SIZE };
      const { data } = await api.get(`/projects/${currentProject.id}/transactions`, { params, signal });
      setTxs(data.items || []); setTotal(data.total || 0);
      setSummary(data.summary || { income: 0, expense: 0, balance: 0 });
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      throw err;
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [currentProject, filters, page, id]);

  useEffect(() => { loadAux(); }, [loadAux, version]);
  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load, version]);

  const save = async (data) => {
    try {
      if (formState?.mode === 'edit' && formState.initial?.id) {
        await api.put(`/transactions/${formState.initial.id}`, data); toast.success('Transação atualizada');
      } else {
        await api.post(`/projects/${currentProject.id}/transactions`, data);
        toast.success(formState?.mode === 'duplicate' ? 'Transação duplicada' : 'Transação criada');
      }
      setFormState(null); setSelected(null); await Promise.all([load(), loadAux()]); bump();
    } catch (err) { toast.error('Erro ao salvar', { description: err?.response?.data?.detail || err.message }); }
  };
  const remove = async (t) => {
    const ok = await confirm({
      title: 'Excluir esta transação?',
      description: `${t.description || '(sem descrição)'} · ${formatBRL(t.amount)}. Os saldos serão recalculados automaticamente.`,
      confirmText: 'Excluir', tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/transactions/${t.id}`); toast.success('Transação excluída');
      setSelected(null); await Promise.all([load(), loadAux()]); bump();
    } catch (err) { toast.error('Erro ao excluir', { description: err?.response?.data?.detail || err.message }); }
  };
  /* Havendo seleção, ela ganha do filtro — ver o comentário em Transactions.jsx.
     Aqui o recorte do recurso (`fixed`) é mantido mesmo com seleção: esta tela
     é sempre "as transações deste pote/conta". */
  const exportarPDF = async () => {
    setExporting(true);
    try {
      const temSelecao = selectedIds.size > 0;
      const params = temSelecao
        ? { ...fixed, limit: 5000, skip: 0 }
        : { ...fixed, ...filtersToParams(filters), limit: 5000, skip: 0 };
      const { data } = await api.get(`/projects/${currentProject.id}/transactions`, { params });
      const lista = temSelecao
        ? (data.items || []).filter((t) => selectedIds.has(t.id))
        : (data.items || []);
      if (lista.length === 0) {
        toast.error('Nada para exportar', { description: 'Nenhuma transação corresponde ao que foi pedido.' });
        return;
      }
      exportTransactionsPDF(lista, `${currentProject.name} — ${item?.name || ''}`, {
        pots, accounts, entities, sources, visibleCols,
      });
    } catch (err) {
      toast.error('Erro ao exportar', { description: err?.response?.data?.detail || err.message });
    } finally { setExporting(false); }
  };
  const createTag = async (name) => {
    const { data } = await api.post(`/projects/${currentProject.id}/tags`, { name });
    setTags((prev) => [...prev, data]); toast.success(`Tag "${data.name}" criada`); bump();
    return data;
  };

  const potName = (pid) => pots.find((x) => x.id === pid)?.name;
  const accName = (aid) => accounts.find((x) => x.id === aid)?.name;
  const entName = (eid) => entities.find((x) => x.id === eid)?.name;
  const srcName = (sid) => sources.find((x) => x.id === sid)?.name;
  const showCol = (c) => visibleCols.has(c);

  if (!currentProject) return <div className="text-slate-500 dark:text-slate-400">Selecione um projeto.</div>;
  if (!item && !loading) return <div className="text-slate-500 dark:text-slate-400">{cfg.label} não encontrado.</div>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      {item && (
        <div className="card p-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold" style={{ background: item.color || '#6366f1' }}>
              {(item.name || '?')[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-500 dark:text-slate-400">{cfg.label}</div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 truncate">{item.name}</h2>
              {item.type && <div className="text-sm text-slate-500 dark:text-slate-400">{item.type}</div>}
            </div>
            {cfg.balanceKey && (
              <div className="text-right">
                <div className="text-xs text-slate-500 dark:text-slate-400">Saldo atual</div>
                <div className={`text-2xl font-bold ${item.balance < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>{formatBRL(item.balance)}</div>
              </div>
            )}
          </div>
        </div>
      )}

      <SummaryCards income={summary.income} expense={summary.expense} expenseCash={summary.expense_cash}
        balance={summary.balance} transfer={summary.transfer} transferCount={summary.transfer_count}
        hide={selectedIds.size > 0 || (!activeFilters && !filters.search.trim())} />

      <SelectionSummary count={selectedIds.size}
        income={selectionTotals.income} expense={selectionTotals.expense} balance={selectionTotals.balance}
        transfer={selectionTotals.transfer} transferCount={selectionTotals.transferCount}
        onClear={clearSelection} />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Buscar transações (valor, data, descrição, tags...)" className="input-md pl-10 pr-10" data-testid="rtx-search-input" />
          {filters.search && (
            <button type="button" onClick={() => setFilters({ ...filters, search: '' })} data-testid="rtx-search-clear-icon"
              aria-label="Limpar busca" title="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <button onClick={() => setShowFilters((v) => !v)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${showFilters || activeFilters ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
          <Filter className="w-4 h-4" /> Filtros
          {activeFilters > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-indigo-600 text-white text-xs font-semibold px-1.5">{activeFilters}</span>
          )}
        </button>
        {activeFilters > 0 && (
          <button onClick={clearFilters}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700">
            <X className="w-4 h-4" /> Limpar
          </button>
        )}
        <button onClick={() => setShowColumns((v) => !v)} data-testid="rtx-columns-toggle"
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${showColumns ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
          <Columns3 className="w-4 h-4" /> Colunas
        </button>
        <button onClick={exportarPDF} disabled={exporting} data-testid="rtx-export"
          title={selectedIds.size > 0
            ? `Exportar as ${selectedIds.size} transações selecionadas`
            : 'Exportar as transações do filtro atual'}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border disabled:opacity-50 ${
            selectedIds.size > 0
              ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
              : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
          }`}>
          <FileDown className="w-4 h-4" />
          {exporting ? '...' : selectedIds.size > 0 ? `Exportar ${selectedIds.size} selecionadas` : 'Exportar'}
        </button>
      </div>

      {showFilters && (
        <FilterBar filters={filters} onChange={setFilters}
          pots={pots} accounts={accounts} entities={entities} sources={sources} tags={tags} />
      )}

      {showColumns && (
        <ColumnsPanel columns={COLUMNS} visible={visibleCols} onToggle={toggleCol} onReset={resetCols} />
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-400">
          {loading ? 'Carregando...' : `Mostrando ${txs.length} de ${total.toLocaleString('pt-BR')} ${total === 1 ? 'transação' : 'transações'}${total > 0 ? ` · Página ${page} de ${Math.ceil(total / PAGE_SIZE)}` : ''}`}
        </div>
        {!loading && txs.length === 0 ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">Sem transações.</div>
        ) : (
          <div className="w-full">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: '44px' }} />
                {COLUMNS.filter((c) => c.id !== 'tags' && showCol(c.id)).map((c) => (
                  <col key={c.id} style={c.width ? { width: c.width } : undefined} />
                ))}
                <col style={{ width: '100px' }} />
              </colgroup>
              <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <tr className="text-left text-slate-600 dark:text-slate-400 align-top">
                  <th className="px-3 py-3 w-[44px]">
                    <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll}
                      className="w-4 h-4 rounded accent-indigo-600 cursor-pointer" data-testid="rtx-select-all" />
                  </th>
                  {showCol('date') && <th className="px-3 py-3">Data</th>}
                  {showCol('description') && <th className="px-3 py-3">Descrição</th>}
                  {showCol('pot_exit') && <th className="px-3 py-3">Pote Saída</th>}
                  {showCol('pot_entry') && <th className="px-3 py-3">Pote Entrada</th>}
                  {showCol('account_exit') && <th className="px-3 py-3">Conta Saída</th>}
                  {showCol('account_entry') && <th className="px-3 py-3">Conta Entrada</th>}
                  {showCol('entity') && <th className="px-3 py-3">Pessoas</th>}
                  {showCol('source') && <th className="px-3 py-3">Fonte de Receita</th>}
                  {showCol('amount') && <th className="px-3 py-3 text-right">Valor</th>}
                  <th className="px-3 py-3 w-[100px]">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {txs.map((t, idx) => (
                  <tr key={t.id} onClick={() => setSelected(t)}
                    className={`hover:bg-slate-50 dark:hover:bg-slate-700/40 cursor-pointer group align-top ${selectedIds.has(t.id) ? 'bg-indigo-50/60 dark:bg-indigo-900/20' : ''}`}>
                    <td className="px-3 py-3 w-[44px]" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(t.id)} readOnly
                        onClick={(e) => clicarCaixa(e, idx)} onMouseDown={evitarSelecaoDeTexto}
                        className="w-4 h-4 rounded accent-indigo-600 cursor-pointer" data-testid={`rtx-select-${t.id}`} />
                    </td>
                    {showCol('date') && (
                      <td className="px-3 py-3 text-xs text-slate-700 dark:text-slate-300 break-words">{formatDateBR(t.transaction_date)}</td>
                    )}
                    {showCol('description') && (
                      <td className="px-3 py-3 text-slate-800 dark:text-slate-200 break-words">
                        <div className="flex items-start gap-2">
                          <span className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md ${t.type==='income'?'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300':t.type==='expense'?'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300':'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`} title={t.type==='income'?'Receita':t.type==='expense'?'Despesa':'Transferência'}>
                            {t.type==='income'?<TrendingUp className="w-3 h-3"/>:t.type==='expense'?<TrendingDown className="w-3 h-3"/>:<ArrowLeftRight className="w-3 h-3"/>}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="break-words">{t.description || '(sem descrição)'}</div>
                            {t.notes && (
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 break-words">{t.notes}</div>
                            )}
                            {showCol('tags') && t.tags?.length > 0 && (
                              <div className="flex gap-1 flex-wrap mt-1.5">
                                {t.tags.map((tg) => <span key={tg.id} className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 break-words">{tg.name}</span>)}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {showCol('pot_exit') && (
                      <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-400 break-words">{potName(t.pot_exit_id) || '-'}</td>
                    )}
                    {showCol('pot_entry') && (
                      <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-400 break-words">{potName(t.pot_entry_id) || '-'}</td>
                    )}
                    {showCol('account_exit') && (
                      <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-400 break-words">{accName(t.account_exit_id) || '-'}</td>
                    )}
                    {showCol('account_entry') && (
                      <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-400 break-words">{accName(t.account_entry_id) || '-'}</td>
                    )}
                    {showCol('entity') && (
                      <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-400 break-words">{entName(t.entity_id) || '-'}</td>
                    )}
                    {showCol('source') && (
                      <td className="px-3 py-3 text-xs text-slate-600 dark:text-slate-400 break-words">{srcName(t.revenue_source_id) || '-'}</td>
                    )}
                    {showCol('amount') && (
                      <td className={`px-3 py-3 text-right font-semibold break-words ${t.type==='income'?'text-emerald-600 dark:text-emerald-400':t.type==='expense'?'text-rose-600 dark:text-rose-400':'text-blue-600 dark:text-blue-400'}`}>
                        {t.type==='expense'?'-':t.type==='income'?'+':''}{formatBRL(t.amount)}
                      </td>
                    )}
                    <td className="px-3 py-3 w-[100px]" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-0.5">
                        <button title="Duplicar" onClick={() => setFormState({ mode: 'duplicate', initial: { ...t, id: undefined } })} className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"><Copy className="w-4 h-4" /></button>
                        <button title="Editar" onClick={() => setFormState({ mode: 'edit', initial: t })} className="p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"><Edit2 className="w-4 h-4" /></button>
                        <button title="Excluir" onClick={() => remove(t)} className="p-1 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
      </div>

      {selected && (
        <TransactionDetailsModal
          transaction={selected} pots={pots} accounts={accounts} entities={entities} sources={sources}
          onClose={() => setSelected(null)}
          onEdit={(t) => { setSelected(null); setFormState({ mode: 'edit', initial: t }); }}
          onDuplicate={(t) => { setSelected(null); setFormState({ mode: 'duplicate', initial: { ...t, id: undefined } }); }}
          onDelete={(t) => remove(t)}
        />
      )}
      {formState && (
        <TransactionFormModal
          initial={formState.initial} mode={formState.mode}
          pots={pots} accounts={accounts} entities={entities} sources={sources} tags={tags}
          onClose={() => setFormState(null)} onSave={save} onCreateTag={createTag}
        />
      )}
    </div>
  );
}
